import { useEffect, useRef, useState } from "react";
import { useCurrentRapidData } from "@rapid/store";
import * as api from "@rapid/utils/api/datasets";
import { useDatabase } from "@rapid/providers/database/DatabaseProvider";

/**
 * Hook to watch the dataset image count in real-time using Firestore listeners.
 *
 * Resolves the dataset ID from the current rapidDataId (via API), then
 * subscribes to the dataset document for live `images` field updates.
 * Follows the same Firestore listener pattern as useReviewBatchCount.
 */
export function useDatasetImageCount() {
    const currentRapidData = useCurrentRapidData();
    const rapidDataId = currentRapidData?.id;
    const [count, setCount] = useState<number | null>(null);
    const [datasetId, setDatasetId] = useState<string | null>(null);
    const resolvedDatasetIdRef = useRef<string | null>(null);
    const database = useDatabase();

    // Resolve rapidDataId -> datasetId (one-time, cached in ref)
    useEffect(() => {
        if (!rapidDataId) {
            setCount(null);
            setDatasetId(null);
            resolvedDatasetIdRef.current = null;
            return;
        }

        let cancelled = false;

        async function resolve() {
            // Use cached value if already resolved for this rapidDataId
            if (resolvedDatasetIdRef.current) {
                return;
            }

            const dataset = await api.getDatasetByRapidDataId(rapidDataId!);
            if (cancelled) return;

            if (dataset) {
                resolvedDatasetIdRef.current = dataset.id;
                setDatasetId(dataset.id);
            }
        }

        resolve();

        return () => {
            cancelled = true;
        };
    }, [rapidDataId]);

    // Subscribe to dataset document for live image count
    useEffect(() => {
        if (!datasetId) {
            return;
        }

        const unsub = database.onSnapshot(
            "datasets",
            datasetId,
            (snap) => {
                if (!snap.exists()) {
                    setCount(0);
                } else {
                    setCount(snap.data().images ?? 0);
                }
            },
            (error) => {
                console.warn("[useDatasetImageCount] Listener error:", error);
            }
        );

        return () => {
            unsub();
        };
    }, [datasetId, database]);

    return { count, datasetId };
}
